import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Lock, ArrowRight } from 'lucide-react';

// Dashboard banner for MSP GPT (Seller Pro) / MIP GPT (Influencer Pro). Click -> opens the tool (or upgrade when locked).
//   <MspGptBanner tool="seller"     locked={!isPlanUnlocked('Pro')} onUpgrade={handleUpgrade} />
//   <MspGptBanner tool="influencer" locked={!isPlanUnlocked('Pro')} onUpgrade={handleUpgrade} />

const COPY = {
    seller: {
        name: 'MSP GPT',
        gradient: 'from-teal-500 via-cyan-500 to-blue-600',
        button: 'from-teal-500 to-blue-600',
    },
    influencer: {
        name: 'MIP GPT',
        gradient: 'from-blue-600 via-indigo-500 to-purple-600',
        button: 'from-blue-600 to-purple-600',
    },
};

// "Version 1.0 is live!!" badge with a confetti burst (on load and on hover)
const CONFETTI_COLORS = ['#14b8a6', '#2563eb', '#a855f7', '#f59e0b', '#ef4444', '#22c55e'];
const LiveBadge = ({ burst }) => {
    const pieces = useMemo(() => Array.from({ length: 24 }, (_, i) => ({
        x: Math.round(Math.cos((i / 24) * Math.PI * 2) * (40 + Math.random() * 50)),
        y: Math.round(Math.sin((i / 24) * Math.PI * 2) * (25 + Math.random() * 35) - 20),
        r: Math.round(Math.random() * 720 - 360),
        c: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        d: Math.round(Math.random() * 150),
        round: i % 3 === 0,
    })), []);
    return (
        <span className="relative inline-flex">
            <span className="text-[10px] font-bold uppercase tracking-widest text-white bg-gradient-to-r from-green-500 via-teal-500 to-blue-600 px-2 py-0.5 rounded-full"
                style={{ animation: 'gptLivePulse 2s ease-in-out infinite' }}>
                🎉 Version 1.0 is live!!
            </span>
            <span key={burst} className="pointer-events-none absolute left-1/2 top-1/2 z-10">
                {pieces.map((p, i) => (
                    <span key={i} className={`absolute block ${p.round ? 'rounded-full w-2 h-2' : 'rounded-sm w-1.5 h-2.5'}`}
                        style={{
                            background: p.c, '--x': `${p.x}px`, '--y': `${p.y}px`, '--r': `${p.r}deg`,
                            animation: `gptConfetti 1.3s cubic-bezier(.2,.7,.4,1) ${p.d}ms forwards`, opacity: 0,
                        }} />
                ))}
            </span>
        </span>
    );
};

const MspGptBanner = ({ tool = 'seller', locked = false, onUpgrade }) => {
    const navigate = useNavigate();
    const [burst, setBurst] = useState(1);              // changing it replays the confetti
    const c = COPY[tool] || COPY.seller;
    const open = () => (locked ? onUpgrade?.() : navigate(`/msp-gpt?tool=${tool}`));

    return (
        <>
            <style>{`
                @keyframes gptShine { 0% { transform: translateX(-120%) skewX(-20deg); } 100% { transform: translateX(420%) skewX(-20deg); } }
                .gpt-shine { animation: gptShine 3.6s ease-in-out infinite; }
                @keyframes gptConfetti { 0% { transform: translate(0,0) rotate(0); opacity: 1; } 100% { transform: translate(var(--x), calc(var(--y) + 40px)) rotate(var(--r)); opacity: 0; } }
                @keyframes gptLivePulse { 0%,100% { box-shadow: 0 0 0 0 rgba(34,197,94,.45); } 50% { box-shadow: 0 0 0 6px rgba(34,197,94,0); } }
            `}</style>
            <div onClick={open} onMouseEnter={() => setBurst(b => b + 1)}
                className={`relative mb-8 rounded-2xl bg-gradient-to-r ${c.gradient} p-[2px] shadow-xl cursor-pointer group`}>
                <div className="relative rounded-[14px] bg-gradient-to-r from-white via-sky-50 to-indigo-50 px-5 py-4 sm:px-6 overflow-hidden flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="gpt-shine pointer-events-none absolute inset-y-0 left-0 w-1/5 bg-gradient-to-r from-transparent via-sky-200/50 to-transparent" />
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${c.gradient} flex items-center justify-center flex-shrink-0`}>
                        <Sparkles className="text-white" size={24} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <h2 className="text-xl font-bold text-gray-800">{c.name}</h2>
                            <LiveBadge burst={burst} />
                        </div>
                        <p className="text-gray-600 text-sm mt-0.5">Describe the products you want. We'll find them for you.</p>
                    </div>
                    <button
                        onClick={(e) => { e.stopPropagation(); open(); }}
                        className={`flex-shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r ${c.button} text-white font-bold shadow-lg group-hover:scale-105 transition`}
                    >
                        {locked ? <><Lock size={16} /> Upgrade to unlock</> : <>Try it now <ArrowRight size={16} /></>}
                    </button>
                </div>
            </div>
        </>
    );
};

export default MspGptBanner;